import { Dispatch, ReactElement, SetStateAction, useState } from "react";
import { Archive, ArchiveRestore } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/_atoms/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/_atoms/dialog";
import { Separator } from "@/components/_atoms/separator";
import { IconTrash } from "@/components/_icons/IconTrash";
import { DeleteConfirmation } from "@/components/_molecules/delete-confirmation";
import { FeatureFlagToggle } from "@/domain/_shared/feature-flag/components/feature-flag-toggle";
import { useDomainTranslation } from "@/domain/_shared/i18n/use-domain-translation";
import { useMoveMunicipalPropertyToTrash } from "@/domain/municipal-property/hooks/api/use-move-municipal-property-to-trash";
import { useMunicipalPropertyHistory } from "@/domain/municipal-property/hooks/api/use-municipal-property-history";
import { useSetMunicipalPropertyArchived } from "@/domain/municipal-property/hooks/api/use-set-municipal-property-archived";
import { useUpdateMunicipalProperty } from "@/domain/municipal-property/hooks/api/use-update-municipal-property";
import { MunicipalPropertyCatalogRow } from "@/domain/municipal-property/models/municipal-property-catalog-row.model";
import { MunicipalPropertyStand } from "@/domain/municipal-property/models/municipal-property-stand.model";
import { MunicipalPropertyStandSubmission } from "@/domain/municipal-property/models/municipal-property-stand-submission.model";
import { resolveNextEditableYear } from "@/domain/municipal-property/models/resolve-next-editable-year";
import { toMunicipalPropertyStand } from "@/domain/municipal-property/models/to-municipal-property-stand";
import { toUpdateMunicipalPropertyInput } from "@/domain/municipal-property/models/to-update-municipal-property-input";
import { useMovedToTrashToast } from "@/domain/trash/hooks/use-moved-to-trash-toast";
import { useCurrentWorkspaceContext } from "@/domain/workspace/hooks/useCurrentWorkspaceContext";
import { extractGraphQLErrorCode } from "@/lib/extract-graphql-error-code";
import { MUNICIPAL_PROPERTY_CONSUMPTION_FEATURE_FLAG_ID } from "@ark/shared";

import { MunicipalPropertyHistoryTimeline } from "./municipal-property-history-timeline";
import { MunicipalPropertyStandForm } from "./municipal-property-stand-form";
import { MunicipalPropertyUsageSection } from "./municipal-property-usage-section";

interface MunicipalPropertyManageDialogProps {
  property: MunicipalPropertyCatalogRow | null;
  onOpenChange: (isOpen: boolean) => void;
}

export function MunicipalPropertyManageDialog({
  property,
  onOpenChange,
}: MunicipalPropertyManageDialogProps): ReactElement | null {
  return property ? (
    <MunicipalPropertyManageDialogContent key={property.id} property={property} onOpenChange={onOpenChange} />
  ) : null;
}

function MunicipalPropertyManageDialogContent({
  property,
  onOpenChange,
}: {
  property: MunicipalPropertyCatalogRow;
  onOpenChange: (isOpen: boolean) => void;
}): ReactElement {
  const { t }: ReturnType<typeof useDomainTranslation> = useDomainTranslation("municipal-property");
  const { currentWorkspace }: ReturnType<typeof useCurrentWorkspaceContext> = useCurrentWorkspaceContext();
  const [selectedYear, setSelectedYear]: [number, Dispatch<SetStateAction<number>>] = useState(
    new Date().getFullYear(),
  );
  const history: ReturnType<typeof useMunicipalPropertyHistory> = useMunicipalPropertyHistory({
    propertyId: property.id,
    year: selectedYear,
  });
  const update: ReturnType<typeof useUpdateMunicipalProperty> = useUpdateMunicipalProperty(currentWorkspace.id);
  const setArchived: ReturnType<typeof useSetMunicipalPropertyArchived> = useSetMunicipalPropertyArchived({
    workspaceId: currentWorkspace.id,
    propertyId: property.id,
  });
  const moveToTrash: ReturnType<typeof useMoveMunicipalPropertyToTrash> = useMoveMunicipalPropertyToTrash({
    workspaceId: currentWorkspace.id,
    propertyId: property.id,
  });
  const notifyMovedToTrash: ReturnType<typeof useMovedToTrashToast> = useMovedToTrashToast();
  const [isDeleteOpen, setIsDeleteOpen]: [boolean, Dispatch<SetStateAction<boolean>>] = useState(false);
  const [savedYearList, setSavedYearList] = useState<readonly number[]>([]);
  const effectiveYearList = history.data?.effectiveYearList ?? savedYearList;

  function handleYearChange(year: number): void {
    setSavedYearList(effectiveYearList);
    setSelectedYear(year);
  }

  async function handleLifecycle(): Promise<void> {
    try {
      if (property.archivedAt) {
        await setArchived.mutateAsync(false);
        toast.success(t("edit.reactivated"));
      } else {
        await setArchived.mutateAsync(true);
        toast.success(t("edit.archived"));
      }
      onOpenChange(false);
    } catch {
      toast.error(t("edit.lifecycleError"));
    }
  }

  async function handleDelete(): Promise<void> {
    try {
      await moveToTrash.mutateAsync();
      notifyMovedToTrash({ entityType: "municipalProperty", idList: [property.id] });
      setIsDeleteOpen(false);
      onOpenChange(false);
    } catch (error) {
      toast.error(t(extractGraphQLErrorCode(error) === "CONFLICT" ? "edit.deleteConflict" : "edit.deleteError"));
    }
  }

  const stand: MunicipalPropertyStand | null = toMunicipalPropertyStand(history.data);

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-5xl gap-0 overflow-y-auto p-0">
        <DialogHeader className="border-b px-6 py-5">
          <DialogTitle>{t("edit.dialogTitle")}</DialogTitle>
          <DialogDescription>{t("edit.dialogDescription")}</DialogDescription>
        </DialogHeader>
        <div className="grid min-w-0 md:grid-cols-[12rem_minmax(0,1fr)]">
          <MunicipalPropertyHistoryTimeline
            effectiveYearList={effectiveYearList}
            selectedYear={history.data?.effectiveYear ?? null}
            isDisabled={history.isFetching || update.isPending}
            onYearChange={handleYearChange}
          />
          <fieldset disabled={history.isFetching} className="min-w-0 p-6">
            {history.isFetching && (
              <p role="status" className="mb-4 text-sm text-muted-foreground">
                {t("catalog.loading")}
              </p>
            )}
            {history.isError && <p role="alert">{t("history.loadError")}</p>}
            {history.data && !history.data.property && (
              <p role="status">{t("history.missing", { year: selectedYear })}</p>
            )}
            {stand && (
              <MunicipalPropertyStandForm
                key={`${stand.effectiveYear}:${stand.standUpdatedAt}`}
                overwriteEffectiveYear={stand.effectiveYear}
                initialEffectiveYear={resolveNextEditableYear(stand.effectiveYearList)}
                municipalityList={currentWorkspace.municipalityList ?? []}
                property={stand.property}
                isUpdating={update.isPending}
                onSubmit={async ({ values, isOverwrite }: MunicipalPropertyStandSubmission): Promise<void> => {
                  await update.mutateAsync(
                    toUpdateMunicipalPropertyInput({ propertyId: property.id, stand, values, isOverwrite }),
                  );
                }}
                onSuccess={(): void => onOpenChange(false)}
                onCancel={(): void => onOpenChange(false)}
              />
            )}
          </fieldset>
        </div>
        <Separator />
        <div className="px-6">
          <FeatureFlagToggle flag={MUNICIPAL_PROPERTY_CONSUMPTION_FEATURE_FLAG_ID} isDevelopmentBypassAllowed={false}>
            <MunicipalPropertyUsageSection propertyId={property.id} />
          </FeatureFlagToggle>
        </div>
        <Separator />
        <div className="flex justify-between px-6 pb-6 pt-4">
          <Button
            variant="outline"
            size="xs"
            className="gap-2 [&_svg]:size-3.5"
            disabled={setArchived.isPending}
            onClick={(): void => void handleLifecycle()}
          >
            {property.archivedAt ? (
              <ArchiveRestore data-icon="inline-start" aria-hidden="true" />
            ) : (
              <Archive data-icon="inline-start" aria-hidden="true" />
            )}
            {t(property.archivedAt ? "edit.reactivate" : "edit.archive")}
          </Button>
          <Button
            variant="destructive"
            size="xs"
            className="gap-2 [&_svg]:size-3.5"
            disabled={moveToTrash.isPending}
            onClick={(): void => setIsDeleteOpen(true)}
          >
            <IconTrash data-icon="inline-start" />
            {t("edit.moveToTrash")}
          </Button>
        </div>
        <DeleteConfirmation
          open={isDeleteOpen}
          onOpenChange={setIsDeleteOpen}
          title={t("edit.deleteConfirmTitle")}
          description={t("edit.deleteConfirmDescription")}
          onConfirm={(): void => void handleDelete()}
          onCancel={(): void => setIsDeleteOpen(false)}
        />
      </DialogContent>
    </Dialog>
  );
}
